import React, { useState } from 'react';
import Papa from 'papaparse';
import { Upload, FileSpreadsheet, AlertCircle, CheckCircle } from 'lucide-react';

interface Step3Props {
  onNext: (data: any) => void;
  onBack: () => void;
  data: any;
  initialFiles?: File[];  // Files from SmartUploader for seamless handoff
}

interface CSVRow {
  [key: string]: string;
}

// Column name aliases for flexible parsing
const COLUMN_ALIASES: Record<string, string[]> = {
  'year': ['year', 'Year', 'YEAR'],
  'month': ['month', 'Month', 'MONTH'],
  'revenue': ['revenue', 'Revenue', 'REVENUE', 'Sales', 'Gross Revenue', 'Total Revenue'],
  'cogs': ['COGS', 'cogs', 'COGS - Variable', 'Cost of Goods Sold', 'Cost of Sales', 'COS'],
  'payroll': ['Payroll', 'payroll', 'Labor', 'labor', 'Employee Wages', 'Wages', 'Variable_Labor', 'Total Labor'],
  'officers_payroll': ['Officers Payroll', 'Officer Compensation', 'Owner Salary', 'Officer Salary', 'Officers Pay'],
  'rent': ['Rent', 'rent', 'Lease', 'Occupancy'],
  'utilities': ['Utilities', 'utilities', 'Utility', 'Utilities_Base'],
  'marketing': ['Marketing', 'marketing', 'Advertising', 'Marketing_Expense'],
  'insurance': ['Insurance', 'insurance'],
  'other_fixed': ['Other_Fixed', 'Other Fixed', 'Other Expenses', 'Miscellaneous'],
};

// Find matching column name from aliases
const findColumn = (headers: string[], targetKey: string): string | null => {
  const aliases = COLUMN_ALIASES[targetKey] || [targetKey];
  for (const alias of aliases) {
    const found = headers.find(h => h.toLowerCase() === alias.toLowerCase());
    if (found) return found;
  }
  return null;
};

export default function Step3_DataEntry({ onNext, onBack, data, initialFiles }: Step3Props) {
  const [fileName, setFileName] = useState<string>('');
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState(false);
  const [rowCount, setRowCount] = useState<number>(0);
  const [detectedYears, setDetectedYears] = useState<number[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  // APPROACH A: Seamless Handoff - auto-process files from SmartUploader
  React.useEffect(() => {
    if (initialFiles && initialFiles.length > 0 && !isProcessing) {
      console.log('📁 Step3_DataEntry: Auto-processing files from SmartUploader');
      setIsProcessing(true);
      // Process the first file (for CSV, we typically only handle one file)
      handleFile(initialFiles[0]);
    }
  }, [initialFiles]);

  const handleFile = (file: File) => {
    setError('');
    setSuccess(false);
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      
      Papa.parse<CSVRow>(text, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          const rows = results.data;
          const headers = results.meta.fields || [];
          
          // ✅ FLEXIBLE: Accept 36-72 months (3-6 years)
          if (rows.length < 36) {
            setError(`Need at least 36 months (3 years) of data. Got ${rows.length} rows. Please provide more historical data.`);
            return;
          }
          if (rows.length > 72) {
            setError(`Maximum 72 months (6 years) supported. Got ${rows.length} rows. Please trim to last 6 years.`);
            return;
          }

          // Find column mappings using aliases
          const yearCol = findColumn(headers, 'year');
          const monthCol = findColumn(headers, 'month');
          const revenueCol = findColumn(headers, 'revenue');
          const cogsCol = findColumn(headers, 'cogs');
          const payrollCol = findColumn(headers, 'payroll');
          const officersCol = findColumn(headers, 'officers_payroll');
          const rentCol = findColumn(headers, 'rent');
          const utilitiesCol = findColumn(headers, 'utilities');
          const marketingCol = findColumn(headers, 'marketing');
          const insuranceCol = findColumn(headers, 'insurance');
          const otherFixedCol = findColumn(headers, 'other_fixed');

          // Validate required columns
          if (!yearCol || !monthCol) {
            setError(`Missing required columns: ${!yearCol ? 'year' : ''} ${!monthCol ? 'month' : ''}. Found columns: ${headers.join(', ')}`);
            return;
          }
          if (!revenueCol) {
            setError(`Missing revenue column. Found columns: ${headers.join(', ')}. Expected: revenue, Revenue, Sales, etc.`);
            return;
          }

          console.log('📊 CSV Column Mapping:', { yearCol, monthCol, revenueCol, cogsCol, payrollCol, officersCol });

          // ✅ FLEXIBLE: Auto-detect years from data
          const yearsInData = new Set<number>();
          rows.forEach(row => {
            const year = parseInt(row[yearCol!]);
            if (year >= 2015 && year <= 2030) {
              yearsInData.add(year);
            }
          });
          
          const sortedYears = Array.from(yearsInData).sort((a, b) => a - b);
          if (sortedYears.length < 3) {
            setError(`Need at least 3 years of data. Found years: ${sortedYears.join(', ')}`);
            return;
          }

          console.log('📅 Detected years:', sortedYears);
          setDetectedYears(sortedYears);

          // Initialize byYear object dynamically
          const byYear: Record<string, any[]> = {};
          sortedYears.forEach(year => {
            byYear[year.toString()] = [];
          });
          
          let valid = true;
          const errors: string[] = [];

          rows.forEach((row, i) => {
            const year = row[yearCol!];
            const month = parseInt(row[monthCol!]);
            
            if (!year || !month || month < 1 || month > 12) {
              errors.push(`Row ${i + 2}: Invalid year/month (${year}/${month})`);
              valid = false;
              return;
            }

            const revenue = parseFloat(row[revenueCol!]) || 0;
            if (revenue === 0) {
              // Warning but don't fail - some months might have zero revenue
              console.warn(`Row ${i + 2}: Zero revenue for ${year}-${month}`);
            }

            // Build expenses object from all expense columns
            const expenses: Record<string, number> = {};
            if (cogsCol) expenses['COGS'] = parseFloat(row[cogsCol]) || 0;
            if (rentCol) expenses['Rent'] = parseFloat(row[rentCol]) || 0;
            if (utilitiesCol) expenses['Utilities'] = parseFloat(row[utilitiesCol]) || 0;
            if (marketingCol) expenses['Marketing'] = parseFloat(row[marketingCol]) || 0;
            if (insuranceCol) expenses['Insurance'] = parseFloat(row[insuranceCol]) || 0;
            if (otherFixedCol) expenses['Other'] = parseFloat(row[otherFixedCol]) || 0;

            // Also capture any other numeric columns as expenses
            headers.forEach((header) => {
              if (header !== yearCol && header !== monthCol && header !== revenueCol && 
                  header !== payrollCol && header !== officersCol &&
                  !Object.values(expenses).includes(parseFloat(row[header]))) {
                const val = parseFloat(row[header]);
                if (!isNaN(val) && val !== 0) {
                  // Skip if already captured via alias
                  const isAlreadyCaptured = Object.keys(expenses).some(k => 
                    COLUMN_ALIASES[k.toLowerCase()]?.includes(header)
                  );
                  if (!isAlreadyCaptured) {
                    expenses[header] = val;
                  }
                }
              }
            });

            // ✅ FLEXIBLE: Handle payroll with or without officers
            const totalPayroll = parseFloat(row[payrollCol!]) || 0;
            const officerComp = officersCol ? (parseFloat(row[officersCol]) || 0) : (totalPayroll * 0.2); // Default 20% if not provided

            if (byYear[year]) {
              byYear[year].push({
                year: parseInt(year),
                month: month,
                revenue: revenue,
                expenses: expenses,
                payroll: {
                  total: totalPayroll,
                  officer_compensation: officerComp
                },
                // Legacy format support
                total_payroll: totalPayroll,
                officer_compensation: officerComp,
              });
            }
          });

          if (!valid && errors.length > 5) {
            setError(errors.slice(0, 3).join('\n') + `\n...and ${errors.length - 3} more errors`);
            return;
          }

          // ✅ FLEXIBLE: Validate months per year (warn but don't fail if some are missing)
          const yearSummary: string[] = [];
          Object.entries(byYear).forEach(([year, arr]) => {
            if (arr.length !== 12) {
              yearSummary.push(`${year}: ${arr.length} months`);
            }
          });
          if (yearSummary.length > 0) {
            console.warn(`⚠️ Incomplete years: ${yearSummary.join(', ')}`);
          }

          // Sort months within each year
          Object.keys(byYear).forEach((year) => {
            byYear[year].sort((a, b) => a.month - b.month);
          });

          setSuccess(true);
          setRowCount(rows.length);
          
          // Build financial_data with detected years
          const financialData: Record<string, any[]> = {};
          sortedYears.forEach(year => {
            financialData[`data_${year}`] = byYear[year.toString()];
          });

          console.log('✅ Parsed financial_data keys:', Object.keys(financialData));
          
          // Pass data to next step
          setTimeout(() => {
            onNext({
              financial_data: financialData,
              detected_years: sortedYears,
              coverage_year: sortedYears[sortedYears.length - 1] // Most recent year
            });
          }, 1000);
        },
        error: (err) => {
          setError(`CSV parse error: ${err.message}`);
        },
      });
    };
    
    reader.onerror = () => {
      setError('Failed to read file');
    };
    
    reader.readAsText(file);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    const fileName = file?.name.toLowerCase();
    const isSupported = fileName?.endsWith('.csv') || 
                       fileName?.endsWith('.xlsx') || 
                       fileName?.endsWith('.xls') ||
                       fileName?.endsWith('.iif') ||
                       fileName?.endsWith('.qbo');
    
    if (file && isSupported) {
      handleFile(file);
    } else {
      setError('Please upload a CSV, Excel (.xlsx, .xls), or QuickBooks (.iif, .qbo) file');
    }
  };

  const onFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-2xl p-8">
      <div className="text-center mb-6">
        <h2 className="text-3xl font-bold mb-2 text-gray-900">Step 3: Upload Financial Data</h2>
        <p className="text-gray-600">Upload CSV, Excel, or QuickBooks file with 3-6 years of monthly revenue and expense data</p>
      </div>

      <div
        className={`border-2 border-dashed rounded-xl p-12 text-center transition-all ${
          isDragging 
            ? 'border-blue-600 bg-blue-50 scale-105' 
            : success
            ? 'border-green-500 bg-green-50'
            : error
            ? 'border-red-500 bg-red-50'
            : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
        }`}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={onDrop}
      >
        {!success && !error && (
          <Upload className={`w-16 h-16 mx-auto mb-4 transition-colors ${isDragging ? 'text-blue-600' : 'text-gray-400'}`} />
        )}
        {success && (
          <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-600" />
        )}
        {error && (
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-600" />
        )}
        
        <p className="text-xl font-semibold mb-2">
          {isDragging && 'Drop your file here!'}
          {!isDragging && !success && !error && '📂 Drag & drop your file here'}
          {success && '✅ File uploaded successfully!'}
          {error && '❌ Upload failed'}
        </p>
        
        {!success && (
          <>
            <p className="text-sm text-gray-500 mb-6">or click to browse your files</p>

            <label className="cursor-pointer">
              <input
                type="file"
                accept=".csv,.xlsx,.xls,.iif,.qbo"
                onChange={onFileSelect}
                className="hidden"
              />
              <span className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition-colors shadow-lg">
                <FileSpreadsheet className="w-5 h-5 mr-2" />
                Choose File
              </span>
            </label>
          </>
        )}

        {fileName && (
          <p className="mt-4 text-sm text-gray-700">
            📄 Selected: <span className="font-medium">{fileName}</span>
          </p>
        )}
      </div>

      {error && (
        <div className="mt-6 flex items-start p-4 bg-red-50 border-2 border-red-300 rounded-lg text-red-800">
          <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5" />
          <div className="whitespace-pre-line text-sm">{error}</div>
        </div>
      )}

      {success && (
        <div className="mt-6 p-6 bg-green-50 border-2 border-green-300 rounded-lg">
          <div className="flex items-center text-green-800 mb-4">
            <CheckCircle className="w-6 h-6 mr-3" />
            <span className="text-lg font-semibold">{rowCount} months loaded successfully!</span>
          </div>
          <ul className="text-sm text-green-700 space-y-2">
            {detectedYears.map(year => (
              <li key={year}>✓ {year}: 12 months</li>
            ))}
          </ul>
          <p className="mt-4 text-sm text-green-600 font-medium">
            Proceeding to next step...
          </p>
        </div>
      )}

      {/* Required CSV Format */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm font-semibold text-blue-900 mb-2">📋 Flexible CSV Format:</p>
        <ul className="text-xs text-blue-800 space-y-1">
          <li>• <strong>Required Columns:</strong> year, month, revenue</li>
          <li>• <strong>Optional Columns:</strong> COGS, Payroll/Labor, Rent, Utilities, Marketing, etc.</li>
          <li>• <strong>Rows:</strong> 36-72 months (3-6 years of data)</li>
          <li>• <strong>Years:</strong> Any consecutive years (e.g., 2020-2024, 2019-2022)</li>
          <li>• <strong>Months:</strong> 1-12 for each year</li>
        </ul>
        <p className="text-xs text-blue-600 mt-2 italic">
          💡 Column names are flexible - we'll auto-detect: Labor=Payroll, COGS=Cost of Sales, etc.
        </p>
      </div>

      {/* Navigation */}
      <div className="flex gap-4 mt-8">
        <button
          onClick={onBack}
          className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold py-3 px-6 rounded-lg transition-colors"
        >
          ← Back
        </button>
        <button
          onClick={() => success && onNext(data)}
          disabled={!success}
          className={`flex-1 font-bold py-3 px-6 rounded-lg transition-colors ${
            success
              ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg cursor-pointer'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          {success ? 'Next →' : 'Upload File to Continue'}
        </button>
      </div>
    </div>
  );
}
